import type { ApiRequestHelpers } from "./useHttpClient";
import { useCurrentLocation } from "#imports";
import type {
  CreateProductPayload,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  PaginationQuery,
  Product,
  Response,
  SortByQuery,
  UpdateProductPayload,
} from "~/types/api";

export function createProductApi({ postRequest, putRequest, deleteRequest, getPaginationRequest }: ApiRequestHelpers) {
  const { currentLocationId } = useCurrentLocation();

  /** Fetches a paginated list of products. */
  const products = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => {
    return getPaginationRequest<Product>("/products", pagination, sortBy, includes, filterBy);
  };

  /** Fetches a paginated list of products filtered by type. */
  const productsByType = (
    type: string,
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
    additionalParams?: Record<string, string | number | boolean>,
  ) => {
    const config = additionalParams ? { params: additionalParams } : {};
    return getPaginationRequest<Product>(
      `/products/by-type?type=${type}`,
      pagination,
      sortBy,
      includes,
      filterBy,
      config,
    );
  };

  /** Creates a new product for the current location. */
  const createProduct = (data: CreateProductPayload) =>
    postRequest<Response<Product>>(`/locations/${currentLocationId.value}/products`, data);

  /** Updates an existing product. */
  const updateProduct = (productId: number, data: UpdateProductPayload) =>
    putRequest<Response<Product>>(`/products/${productId}`, data);

  /** Deletes a product by ID. */
  const deleteProduct = (productId: number) => deleteRequest<EmptyResponse>(`/products/${productId}`);

  return { products, productsByType, createProduct, updateProduct, deleteProduct };
}

export type ProductApi = ReturnType<typeof createProductApi>;
